import { ApiClient } from '../core/ApiClient';
import { PkceService } from './PkceService';
import { ENDPOINTS } from '../constants/endpoints';
import { STORAGE_KEYS } from '../constants/storage';
import { TokenHelper } from '@/utils/tokenHelper';

export class AuthService {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Generates PKCE parameters, saves them, and returns the IdP authorization URL.
   */
  async createAuthorizationUrl(redirectUri: string, prompt?: string, resource?: string): Promise<string> {
    const verifier = PkceService.generateVerifier();
    const challenge = await PkceService.generateChallenge(verifier);
    
    const state = Math.random().toString(36).substring(2, 15);
    this.apiClient.storage.set(STORAGE_KEYS.AUTH_VERIFIER, verifier);
    this.apiClient.storage.set(STORAGE_KEYS.AUTH_STATE, state);
    this.apiClient.storage.set(STORAGE_KEYS.AUTH_REDIRECT_URI, redirectUri);

    const authorizeEndpoint = `${this.apiClient.config.idpBaseUrl}${ENDPOINTS.AUTHORIZE}`;
    
    const authUrl = new URL(authorizeEndpoint);
    authUrl.searchParams.append('client_id', this.apiClient.config.clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    if (resource) authUrl.searchParams.append('resource', resource);
    authUrl.searchParams.append('code_challenge', challenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    authUrl.searchParams.append('state', state);
    if (prompt) authUrl.searchParams.append('prompt', prompt);

    return authUrl.toString();
  }

  /**
   * Validates the callback state, exchanges the code for a token, and returns the original URL.
   */
  async completeAuthorization(code: string, state: string, currentRedirectUri: string): Promise<string> {
    const savedState = this.apiClient.storage.get(STORAGE_KEYS.AUTH_STATE);
    const savedRedirectUri = this.apiClient.storage.get(STORAGE_KEYS.AUTH_REDIRECT_URI);

    if (!savedState || state !== savedState) {
      throw new Error('Invalid state or state not found in session.');
    }

    this.apiClient.storage.remove(STORAGE_KEYS.AUTH_STATE);
    this.apiClient.storage.remove(STORAGE_KEYS.AUTH_REDIRECT_URI);

    const verifier = this.apiClient.storage.get(STORAGE_KEYS.AUTH_VERIFIER);
    this.apiClient.storage.remove(STORAGE_KEYS.AUTH_VERIFIER);

    if (!verifier) {
      throw new Error('PKCE verifier not found in session.');
    }

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', this.apiClient.config.clientId);
    params.append('redirect_uri', currentRedirectUri);
    params.append('code', code);
    params.append('code_verifier', verifier);

    const response = await this.apiClient.oAuth2AxiosInstance.post(
      ENDPOINTS.TOKEN,
      params.toString(),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );

    const data = this.getTokenResponse(response.data);
    this.setAccessToken(data.access_token);
    return savedRedirectUri || '/';
  }

  private refreshPromise: Promise<string> | null = null;

  /**
   * Refreshes the access token using the HttpOnly refresh token cookie via the proxy.
   */
  async refreshAccessToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.append('grant_type', 'refresh_token');
        params.append('client_id', this.apiClient.config.clientId);

        const response = await this.apiClient.oAuth2AxiosInstance.post(
          ENDPOINTS.TOKEN,
          params.toString(),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        );

        const data = this.getTokenResponse(response.data);
        const token = data.access_token;
        this.setAccessToken(token);
        return token;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }


  /**
   * Clears the local session, invalidates the session, and returns the IdP end-session URL.
   */
  async logout(postLogoutRedirectUri?: string, skipEndSession: boolean = true, idTokenHint?: string, state?: string): Promise<string> {
    const token = this.getAccessToken();

    // Remove stored keys
    TokenHelper.removeTokens(this.apiClient.storage);

  // Call proxy logout endpoint
    const params = new URLSearchParams();
    params.append('client_id', this.apiClient.config.clientId);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      await this.apiClient.oAuth2AxiosInstance.post(ENDPOINTS.LOGOUT, params.toString(), { headers });
    } catch (e) {
      console.error('Failed to make logout network call', e);
    }

    if (skipEndSession) {
      return '';
    }

    // Generate end-session URL
    const endSessionEndpoint = `${this.apiClient.config.idpBaseUrl}${ENDPOINTS.END_SESSION}`;
    const url = new URL(endSessionEndpoint);

    if (postLogoutRedirectUri) {
      url.searchParams.append('post_logout_redirect_uri', postLogoutRedirectUri);
    }
    if (idTokenHint) {
      url.searchParams.append('id_token_hint', idTokenHint);
    }
    if (state) {
      url.searchParams.append('state', state);
    }

    return url.toString();
  }

  getAccessToken(): string | null {
    return this.apiClient.storage.get(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setAccessToken(token: string): void {
    this.apiClient.storage.set(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  private getTokenResponse(responseData: any): any {
    return responseData?.data ?? responseData;
  }
}
